import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { employerAPI } from '../services/api';

const ROLE_OPTIONS = [
  { value: 'hr_recruiter', label: 'HR Recruiter' },
  { value: 'hr_manager', label: 'HR Manager' },
];

const roleLabel = (value) => {
  const match = ROLE_OPTIONS.find((option) => option.value === value);
  return match ? match.label : value;
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateString));
  } catch (error) {
    return dateString;
  }
};

const EmployerEmployees = ({ canManage = false }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [roleUpdateInFlight, setRoleUpdateInFlight] = useState(null);
  const [statusUpdateInFlight, setStatusUpdateInFlight] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'hr_recruiter',
  });

  const activeEmployees = useMemo(
    () => employees.filter((employee) => employee.isActive),
    [employees]
  );

  const inactiveEmployees = useMemo(
    () => employees.filter((employee) => !employee.isActive),
    [employees]
  );

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await employerAPI.getEmployees();
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Failed to load employees:', error);
      toast.error('Unable to load employees. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'hr_recruiter',
    });
  };

  const handleCreateEmployee = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await employerAPI.createEmployee(formData);
      toast.success('Employee invitation sent successfully.');
      setShowModal(false);
      resetForm();
      loadEmployees();
    } catch (error) {
      console.error('Failed to create employee:', error);
      const message = error.response?.data?.detail || 'Failed to create employee.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (employeeId, newRole) => {
    setRoleUpdateInFlight(employeeId);
    try {
      const response = await employerAPI.updateEmployee(employeeId, { role: newRole });
      setEmployees((prev) =>
        prev.map((employee) =>
          employee.id === employeeId ? response.data : employee
        )
      );
      toast.success('Role updated.');
    } catch (error) {
      console.error('Failed to update employee role:', error);
      const message = error.response?.data?.detail || 'Failed to update role.';
      toast.error(message);
    } finally {
      setRoleUpdateInFlight(null);
    }
  };

  const handleStatusToggle = async (employeeId, isActive) => {
    setStatusUpdateInFlight(employeeId);
    try {
      const response = await employerAPI.updateEmployee(employeeId, { isActive });
      setEmployees((prev) =>
        prev.map((employee) =>
          employee.id === employeeId ? response.data : employee
        )
      );
      toast.success(isActive ? 'Employee reactivated.' : 'Employee deactivated.');
    } catch (error) {
      console.error('Failed to update employee status:', error);
      const message = error.response?.data?.detail || 'Failed to update status.';
      toast.error(message);
    } finally {
      setStatusUpdateInFlight(null);
    }
  };

  const handleDeactivate = async (employeeId) => {
    setStatusUpdateInFlight(employeeId);
    try {
      const response = await employerAPI.deleteEmployee(employeeId);
      setEmployees((prev) =>
        prev.map((employee) =>
          employee.id === employeeId ? response.data : employee
        )
      );
      toast.success('Employee deactivated.');
    } catch (error) {
      console.error('Failed to deactivate employee:', error);
      const message = error.response?.data?.detail || 'Failed to deactivate employee.';
      toast.error(message);
    } finally {
      setStatusUpdateInFlight(null);
    }
  };

  return (
    <div className="ui-card p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
          <p className="text-sm text-gray-500">
            Manage recruiting staff with access to your organisation&apos;s hiring workspace.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Employee
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          <EmployeeTable
            title="Active"
            employees={activeEmployees}
            canManage={canManage}
            roleUpdateInFlight={roleUpdateInFlight}
            statusUpdateInFlight={statusUpdateInFlight}
            onRoleChange={handleRoleChange}
            onDeactivate={handleDeactivate}
            onReactivate={(employeeId) => handleStatusToggle(employeeId, true)}
            showEmptyState={activeEmployees.length === 0}
            emptyMessage="No active team members yet."
          />

          {inactiveEmployees.length > 0 && (
            <EmployeeTable
              title="Inactive"
              employees={inactiveEmployees}
              canManage={canManage}
              roleUpdateInFlight={roleUpdateInFlight}
              statusUpdateInFlight={statusUpdateInFlight}
              onRoleChange={handleRoleChange}
              onDeactivate={handleDeactivate}
              onReactivate={(employeeId) => handleStatusToggle(employeeId, true)}
              allowReactivate
            />
          )}
        </div>
      )}

      {showModal && (
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            if (!submitting) {
              setShowModal(false);
            }
          }}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Invite New Employee</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={submitting}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form className="space-y-4" onSubmit={handleCreateEmployee}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(event) => setFormData((prev) => ({ ...prev, firstName: event.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(event) => setFormData((prev) => ({ ...prev, lastName: event.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(event) => setFormData((prev) => ({ ...prev, role: event.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-60"
                >
                  {submitting ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const EmployeeTable = ({
  title,
  employees,
  canManage,
  roleUpdateInFlight,
  statusUpdateInFlight,
  onRoleChange,
  onDeactivate,
  onReactivate,
  showEmptyState = false,
  emptyMessage = 'No employees to display.',
  allowReactivate = false,
}) => {
  if (!employees.length) {
    return showEmptyState ? (
      <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-500 text-sm">
        {emptyMessage}
      </div>
    ) : null;
  }

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Joined</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {employees.map((employee) => (
              <tr key={employee.id}>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">
                    {employee.firstName} {employee.lastName}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{employee.email}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {canManage ? (
                    <select
                      value={employee.role}
                      onChange={(event) => onRoleChange(employee.id, event.target.value)}
                      disabled={roleUpdateInFlight === employee.id}
                      className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    roleLabel(employee.role)
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{formatDate(employee.createdAt)}</td>
                <td className="px-4 py-3 text-sm text-gray-600 text-right">
                  {canManage ? (
                    <div className="inline-flex items-center gap-2">
                      {allowReactivate && !employee.isActive ? (
                        <button
                          onClick={() => onReactivate(employee.id)}
                          disabled={statusUpdateInFlight === employee.id}
                          className="px-3 py-1 border border-green-200 text-green-600 rounded-md hover:bg-green-50 transition-colors disabled:opacity-60"
                        >
                          Reactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => onDeactivate(employee.id)}
                          disabled={statusUpdateInFlight === employee.id}
                          className="px-3 py-1 border border-red-200 text-red-600 rounded-md hover:bg-red-50 transition-colors disabled:opacity-60"
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {employee.isActive ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployerEmployees;

